import { estUnRemplacementPartout, type CanvasActionInput, type CanvasActionName } from '@hector/shared-types';
import { executeCopyFormat } from './copyFormatExecutor';
import { executeWriteMarkdown } from './documentWriteExecutor';
import { executeApplyEditPlan } from './editPlanExecutor';
import {
  executeAddHyperlink,
  executeCreateTable,
  executeInsertHeading,
  executeInsertPageBreak,
} from './formatActionExecutors';
import { executeFormatParagraphs } from './formatParagraphsExecutor';
import { executeFormatText } from './formatTextExecutor';
import { executeReplaceTextEverywhere } from './replaceEverywhereExecutor';
import {
  executeAddListItems,
  executeAttachNumbering,
  executeConvertList,
} from './listActionExecutors';
import {
  executeAddComments,
  executeReplyToComment,
  executeResolveComments,
} from './commentActionExecutors';
import {
  executeDeleteFootnote,
  executeEditFootnote,
  executeInsertFootnote,
} from './footnoteActionExecutors';
import { executeEditImage, executeInsertImage } from './imageActionExecutors';
import { executeSetLetterhead } from './letterheadActionExecutors';
import { executeSetHeaderFooter } from './headerFooterActionExecutors';
import { executeSetPageLayout } from './pageLayoutExecutor';
import { executeSetReviewMode } from './reviewModeActionExecutor';
import { executeRewriteBlock } from './rewriteBlockExecutor';
import { executeApplyStyle, executeInsertParagraphs } from './styleActionExecutors';
import { executeEditTable } from './tableStructureExecutor';
import { CANVAS_TEXT_ACTION_EXECUTORS, type CanvasActionContext } from './textActionExecutors';
import type { CanvasActionReceipt } from './actionReceipt';

export type CanvasActionExecutor = (ctx: CanvasActionContext, input: never) => Promise<CanvasActionReceipt>;

/**
 * `replace_text` a deux exécuteurs, que le seul drapeau `everywhere` départage :
 * un passage isolé dans un bloc (`executeReplaceText`), ou chaque occurrence de
 * plusieurs textes dans tout l'acte, par un plan atomique
 * (`executeReplaceTextEverywhere`). L'aiguillage vit ici, pas dans l'un des deux :
 * ils n'ont ni la même preuve de lecture ni le même reçu.
 */
function executeReplaceTextSelonSonMode(ctx: CanvasActionContext, input: Extract<CanvasActionInput, { action: 'replace_text' }>): Promise<CanvasActionReceipt> {
  return estUnRemplacementPartout(input) ? executeReplaceTextEverywhere(ctx, input) : CANVAS_TEXT_ACTION_EXECUTORS.replace_text(ctx, input);
}

/**
 * ⚠️ Le `satisfies Record<CanvasActionName, …>` EST la garde « catalogue ≡
 * exécuteurs » de #987, et elle vaut mieux qu'un test : un verbe ajouté au
 * catalogue partagé sans exécuteur ici, ou un exécuteur enregistré sous un nom
 * absent du catalogue, casse `check-types` — donc avant qu'aucun tour d'agent
 * n'ait pu être dépensé dessus. Ne pas la remplacer par un `Record<string, …>`
 * ni par une assertion : l'un comme l'autre rendraient l'écart silencieux.
 */
export const CANVAS_ACTION_EXECUTORS = {
  ...CANVAS_TEXT_ACTION_EXECUTORS,
  replace_text: executeReplaceTextSelonSonMode,
  rewrite_block: executeRewriteBlock,
  apply_edit_plan: executeApplyEditPlan,
  insert_paragraphs: executeInsertParagraphs,
  apply_style: executeApplyStyle,
  write_markdown: executeWriteMarkdown,
  format_text: executeFormatText,
  format_paragraphs: executeFormatParagraphs,
  copy_format: executeCopyFormat,
  insert_heading: executeInsertHeading,
  insert_page_break: executeInsertPageBreak,
  add_hyperlink: executeAddHyperlink,
  create_table: executeCreateTable,
  convert_list: executeConvertList,
  add_list_items: executeAddListItems,
  attach_numbering: executeAttachNumbering,
  edit_table: executeEditTable,
  add_comments: executeAddComments,
  reply_to_comment: executeReplyToComment,
  resolve_comments: executeResolveComments,
  insert_footnote: executeInsertFootnote,
  edit_footnote: executeEditFootnote,
  delete_footnote: executeDeleteFootnote,
  set_letterhead: executeSetLetterhead,
  set_header_footer: executeSetHeaderFooter,
  set_page_layout: executeSetPageLayout,
  insert_image: executeInsertImage,
  edit_image: executeEditImage,
  set_review_mode: executeSetReviewMode,
} satisfies Record<CanvasActionName, CanvasActionExecutor>;
